import { useState, useEffect } from 'react';
import { RefreshCw, Activity, CheckCircle2, XCircle, AlertTriangle, Settings, Cpu, Bell, BellOff } from 'lucide-react';
import api from '../api/api';

const STATUS_CFG = {
  running: { l: 'En marcha', cls: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500', pulsing: true },
  stopped: { l: 'Parado', cls: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400', pulsing: false },
  fault: { l: 'Avería', cls: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', pulsing: true },
  maintenance: { l: 'Mantenimiento', cls: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-400', pulsing: false },
  standby: { l: 'Espera', cls: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-400', pulsing: false },
};

const ALARM_SEV = {
  critical: { cls: 'border-red-500 bg-red-50', badge: 'bg-red-600 text-white' },
  high: { cls: 'border-orange-400 bg-orange-50', badge: 'bg-orange-500 text-white' },
  medium: { cls: 'border-yellow-400 bg-yellow-50', badge: 'bg-yellow-400 text-gray-900' },
  low: { cls: 'border-blue-300 bg-blue-50', badge: 'bg-blue-200 text-blue-800' },
};

export default function SCADA() {
  const [equipment, setEquipment] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('status');
  const [section, setSection] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [eqR, alR] = await Promise.all([api.get('/scada/status'), api.get('/scada/alarms')]);
      setEquipment(eqR.data);
      setAlarms(alR.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleAckAlarm = async (id) => {
    await api.put(`/scada/alarms/${id}`, { status: 'acknowledged' });
    load();
  };

  const sections = [...new Set(equipment.map(e => e.section).filter(Boolean))];
  const filtered = section ? equipment.filter(e => e.section === section) : equipment;

  const counts = {
    running: equipment.filter(e => e.status === 'running').length,
    stopped: equipment.filter(e => e.status === 'stopped').length,
    fault: equipment.filter(e => e.status === 'fault').length,
    alarms_active: alarms.filter(a => a.status === 'active').length,
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Monitor SCADA</h1>
          <p className="text-sm text-gray-500 mt-0.5">Estado en tiempo real de la planta fija — Ecoparque de Cáceres</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAutoRefresh(a => !a)} className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-colors ${autoRefresh ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-testid="auto-refresh-btn">
            {autoRefresh ? <Bell size={13} /> : <BellOff size={13} />} Auto (30s)
          </button>
          <button onClick={load} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500" data-testid="refresh-scada-btn"><RefreshCw size={16} /></button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          { icon: CheckCircle2, l: 'En marcha', v: counts.running, c: '#16a34a' },
          { icon: Activity, l: 'Parados', v: counts.stopped, c: '#6b7280' },
          { icon: XCircle, l: 'Con avería', v: counts.fault, c: '#ef4444' },
          { icon: AlertTriangle, l: 'Alarmas activas', v: counts.alarms_active, c: '#f59e0b' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <div key={s.l} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: s.c + '15' }}><Icon size={18} style={{ color: s.c }} /></div>
              <div><p className="text-xs text-gray-500">{s.l}</p><p className="text-xl font-bold" style={{ fontFamily: 'Barlow, sans-serif', color: s.c }}>{s.v}</p></div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[{ k: 'status', l: `Estado Equipos (${equipment.length})` }, { k: 'alarms', l: `Alarmas (${alarms.length})` }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? 'border-[#16a34a] text-[#16a34a]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{t.l}</button>
        ))}
      </div>

      {tab === 'status' && (
        <div>
          {/* Section filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <button onClick={() => setSection('')} className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${!section ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>Todas las secciones</button>
            {sections.map(s => <button key={s} onClick={() => setSection(s)} className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${section === s ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>{s}</button>)}
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map(eq => {
                const cfg = STATUS_CFG[eq.status] || STATUS_CFG.stopped;
                return (
                  <div key={eq.id} className={`bg-white rounded-xl border p-4 shadow-sm transition-shadow hover:shadow-md ${cfg.cls}`} data-testid={`scada-eq-${eq.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                          {cfg.pulsing && <div className={`absolute inset-0 rounded-full ${cfg.dot} animate-ping opacity-60`} />}
                        </div>
                        <Cpu size={14} className="text-gray-500" />
                      </div>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${cfg.cls}`}>{cfg.l}</span>
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 leading-tight mb-0.5">{eq.equipment_name}</h3>
                    <p className="text-[11px] text-gray-400 font-mono mb-2">{eq.equipment_code}</p>
                    {eq.section && <p className="text-[11px] text-gray-500 mb-1">{eq.section}</p>}
                    {eq.variable_name && eq.variable_value !== null && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="text-[10px] text-gray-400">{eq.variable_name}</p>
                        <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>{eq.variable_value} {eq.variable_unit}</p>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400 mt-2">Actualizado: {formatTime(eq.last_update)}</p>
                    {eq.notes && <p className="text-[10px] text-orange-600 mt-1 italic">{eq.notes}</p>}
                  </div>
                );
              })}
              {filtered.length === 0 && <div className="col-span-4 bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Sin equipos en esta sección</div>}
            </div>
          )}
        </div>
      )}

      {tab === 'alarms' && (
        <div>
          <div className="flex gap-2 mb-4">
            {['active', 'acknowledged', 'resolved'].map(s => {
              const count = alarms.filter(a => a.status === s).length;
              return <span key={s} className={`text-xs px-2 py-1 rounded-lg border ${s === 'active' ? 'bg-red-50 border-red-200 text-red-700' : s === 'acknowledged' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>{count} {s === 'active' ? 'Activas' : s === 'acknowledged' ? 'Reconocidas' : 'Resueltas'}</span>;
            })}
          </div>
          <div className="space-y-2">
            {alarms.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <CheckCircle2 size={32} className="text-green-500 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Sin alarmas registradas</p>
              </div>
            ) : alarms.map(alarm => {
              const sev = ALARM_SEV[alarm.severity] || ALARM_SEV.medium;
              return (
                <div key={alarm.id} className={`border-l-4 rounded-r-xl p-4 flex items-start justify-between gap-3 ${sev.cls}`} data-testid={`alarm-${alarm.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${sev.badge}`}>{alarm.severity}</span>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${alarm.status === 'active' ? 'bg-red-100 text-red-700' : alarm.status === 'acknowledged' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'}`}>{alarm.status === 'active' ? 'Activa' : alarm.status === 'acknowledged' ? 'Reconocida' : 'Resuelta'}</span>
                      <span className="text-[10px] text-gray-400">{formatTime(alarm.created_at)}</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{alarm.name || alarm.title || 'Alarma SCADA'}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{alarm.description || alarm.message || ''}</p>
                    {alarm.equipment_name && <p className="text-[11px] text-gray-400 mt-1">Equipo: {alarm.equipment_name}</p>}
                  </div>
                  {alarm.status === 'active' && (
                    <button onClick={() => handleAckAlarm(alarm.id)} className="flex-shrink-0 text-xs px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium whitespace-nowrap" data-testid={`ack-alarm-${alarm.id}`}>Reconocer</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
